<template>
  <div class="p-fluid">
    <div class="field-grid">
      <!-- Title -->
      <div class="field">
        <label for="bug-title">Title *</label>
        <InputText 
          id="bug-title"
          v-model="form.title" 
          placeholder="Brief description of the issue"
          :class="{ 'p-invalid': !form.title && showValidation }"
          @input="$emit('update:title', form.title)"
        />
        <small v-if="!form.title && showValidation" class="p-error">Title is required</small>
      </div>

      <!-- Category -->
      <div class="field">
        <label for="bug-category">Category</label>
        <Dropdown 
          id="bug-category"
          v-model="form.category" 
          :options="categories" 
          option-label="label" 
          option-value="value"
          placeholder="Select category"
          @change="$emit('update:category', form.category)"
        />
      </div>

      <!-- Description -->
      <div class="field">
        <label for="bug-description">Description (optional)</label>
        <Textarea 
          id="bug-description"
          v-model="form.description" 
          rows="4" 
          placeholder="Detailed description, steps to reproduce, etc."
          auto-resize
          @input="$emit('update:description', form.description)"
        />
      </div>

      <!-- Current Page Info (only show if includeContext is true) -->
      <div v-if="includeContext" class="field">
        <label>Context</label>
        <div class="p-3 bg-surface-100 border-round text-sm">
          <span class="text-surface-600">{{ currentRoute }}</span>
        </div>
      </div>

      <!-- Anonymous submission note -->
      <div v-if="showAnonymousNote" class="field">
        <div class="p-3 bg-surface-50 border-round">
          <p class="text-sm text-surface-600 m-0">
            <strong>Note:</strong> You can submit issues anonymously. 
            {{ isAuthenticated ? 'Since you\'re logged in, the issue will be linked to your account.' : 'Log in to track your submitted issues.' }}
          </p>
        </div>
      </div>
    </div>

    <!-- Action Buttons -->
    <div class="button-container mt-4" v-if="showActions">
      <Button 
        v-if="showCancel"
        label="Cancel" 
        icon="pi pi-times" 
        @click="$emit('cancel')" 
        text
        class="cancel-button"
      />
      <Button 
        label="Submit Bug Report" 
        icon="pi pi-check" 
        @click="handleSubmit"
        :loading="submitting"
        :disabled="!form.title.trim()"
        class="submit-button"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Dropdown from 'primevue/dropdown'
import Textarea from 'primevue/textarea'

// Props
interface Props {
  title?: string
  category?: string
  description?: string
  includeContext?: boolean
  showAnonymousNote?: boolean
  showActions?: boolean
  showCancel?: boolean
  submitting?: boolean
  showValidation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  category: 'general',
  description: '',
  includeContext: false,
  showAnonymousNote: false,
  showActions: true,
  showCancel: false,
  submitting: false,
  showValidation: false
})

// Emits
const emit = defineEmits<{
  'update:title': [value: string]
  'update:category': [value: string] 
  'update:description': [value: string]
  'submit': [formData: { title: string, category: string, description: string }]
  'cancel': []
}>()

// Composables
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

// Reactive data
const categories = ref<any[]>([])

// Form data (local state that syncs with props)
const form = ref({
  title: props.title,
  category: props.category,
  description: props.description
})

// Computed
const currentRoute = computed(() => {
  return `${String(route.name)} (${route.path})`
})

const isAuthenticated = computed(() => authStore.isAuthenticated)

// Watch props to update form when they change
watch(() => props.title, (newVal) => {
  form.value.title = newVal
})

watch(() => props.category, (newVal) => {
  form.value.category = newVal
})

watch(() => props.description, (newVal) => {
  form.value.description = newVal
})

// Methods
const fetchCategories = async () => {
  try {
    const response = await fetch('/api/issues/choices/')
    if (response.ok) {
      const data = await response.json()
      categories.value = data.categories || [
        { label: 'General', value: 'general' },
        { label: 'Frontend', value: 'frontend' },
        { label: 'Backend', value: 'backend' },
        { label: 'API', value: 'api' },
        { label: 'UI/UX', value: 'ui_ux' },
        { label: 'Performance', value: 'performance' },
        { label: 'Other', value: 'other' }
      ]
    }
  } catch (error) {
    console.error('Failed to fetch categories:', error)
    // Fallback categories
    categories.value = [
      { label: 'General', value: 'general' },
      { label: 'Frontend', value: 'frontend' },
      { label: 'Backend', value: 'backend' },
      { label: 'API', value: 'api' },
      { label: 'UI/UX', value: 'ui_ux' },
      { label: 'Performance', value: 'performance' },
      { label: 'Other', value: 'other' }
    ]
  }
}

const handleSubmit = () => {
  emit('submit', { ...form.value })
}

// Lifecycle
onMounted(() => {
  fetchCategories()
})

// Expose methods and data for parent component
defineExpose({
  fetchCategories,
  form
})
</script>

<style scoped>
.field-grid {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.field {
  display: flex;
  flex-direction: column;
}

.field label {
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--p-text-color);
}

.button-container {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
}

.cancel-button,
.submit-button {
  flex: 1;
  min-width: 0;
}
</style>
